import { __awaiter } from "tslib";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { ErrorMessage, EmailValidationResponseStatus, AuthFormStep, } from '@as-react/common-types';
import { debouncedPromise } from '@as-react/common-utils';
import { Banner, Box, TextAlpha } from '@yonderland/andes-react';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import CustomerApi from '../../../api/customer/CustomerApi';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import FormValidationUtil from '../../../util/FormValidationUtil';
import { HtmlInclude } from '../../common/html-include/HtmlInclude';
import { EmailChangeHandler } from '../../email/EmailChangeHandler';
import { EmailError } from '../../email/EmailError';
import { validateEmailWithBackend, EMAIL_VALIDATION_DEBOUNCE } from '../../email/EmailValidationUtil';
import { DpeAuthForm } from '../dpe-auth-form/DpeAuthForm';
const DpeAuth = ({ step, data, onStepUpdate, onLogin, onValidationError, loginState, }) => {
    const { servicesApi: apiPrefix, lang, defaultRequestParams } = useAemConfig();
    const [responseErrorMessage, setResponseErrorMessage] = useState(null);
    const [responseSuccessMessage, setResponseSuccessMessage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [customerTitles, setCustomerTitles] = useState([]);
    const [customerGenders, setCustomerGenders] = useState([]);
    const [emailFieldValue, setEmailFieldValue] = useState();
    const [suggestedEmail, setSuggestedEmail] = useState();
    const [skipEmailValidation, setSkipEmailValidation] = useState(false);
    const [emailStatus, setEmailStatus] = useState();
    const { t } = useTranslation([
        I18N_NAMESPACES.account,
        I18N_NAMESPACES.buy,
        I18N_NAMESPACES.dpe,
        I18N_NAMESPACES.navigation,
    ]);
    const setFormStep = (to) => {
        if (to !== step) {
            onStepUpdate({
                from: step,
                to,
            });
            setResponseErrorMessage(null);
            setResponseSuccessMessage(null);
        }
    };
    const emailExists = (email) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const response = yield CustomerApi.emailExists(apiPrefix, defaultRequestParams, email, false);
            return response.knownForFascia;
        }
        catch (_a) {
            return false;
        }
    });
    const handleLoginSubmit = (values) => {
        onLogin({
            username: values.email,
            password: values.password,
            keepSignedIn: true,
        });
    };
    const getTitlesFromApi = () => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield CustomerApi.getTitles(apiPrefix, defaultRequestParams, lang);
        const sortedTitles = response.sort((t1, t2) => t1.sortOrder < t2.sortOrder ? -1 : 1);
        setCustomerTitles(sortedTitles);
    });
    const getGendersFromApi = () => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield CustomerApi.getGenders(apiPrefix, defaultRequestParams, lang);
        setCustomerGenders(response);
    });
    const handleRegistrationSubmit = (values) => {
        const { email, password, title, firstName, lastName, gender, mailSubscribe, emailSubscribe } = values;
        setLoading(true);
        CustomerApi.create(apiPrefix, defaultRequestParams, {
            email,
            firstName,
            gender,
            idTitle: Number(title),
            language: lang,
            lastName,
            password,
            preference: {
                preference: !!mailSubscribe,
                exploreMoreNewsletter: !!emailSubscribe,
            },
        })
            .then(() => onLogin({
            username: email,
            password,
            keepSignedIn: true,
        }))
            .catch(e => {
            var _a;
            const errorMessage = ((_a = e.response.data[0]) === null || _a === void 0 ? void 0 : _a.field) === 'password'
                ? `${t('password.indicator.strength.label')}${t('password.indicator.strength.short')}`
                : t(ErrorMessage.GENERAL_VITALITY);
            onValidationError({
                registration_api: errorMessage,
            });
            setResponseErrorMessage(errorMessage);
        })
            .then(() => {
            setLoading(false);
        });
    };
    const handlePasswordForgottenSubmit = (values) => {
        setLoading(true);
        CustomerApi.sendPasswordResetMail(apiPrefix, defaultRequestParams, values.email)
            .then(() => {
            setFormStep(AuthFormStep.LOGIN);
            setResponseSuccessMessage(t('navigation:flyout.account.reset.password.success.operation'));
        })
            .catch(() => {
            onValidationError({
                passwordForgotton_api: t(ErrorMessage.GENERAL_VITALITY),
            });
            setResponseErrorMessage(t(ErrorMessage.GENERAL_VITALITY));
        })
            .then(() => {
            setLoading(false);
        });
    };
    const validateEmailField = useCallback(debouncedPromise((email, context) => __awaiter(void 0, void 0, void 0, function* () {
        if (skipEmailValidation) {
            return true;
        }
        const isRegistrationStep = step === AuthFormStep.REGISTRATION;
        if (!email) {
            return context.createError({
                message: isRegistrationStep ? EmailValidationResponseStatus.REQUIRED : t('account:delivery.required'),
            });
        }
        else if (!FormValidationUtil.isValidEmailPattern(email)) {
            return context.createError({
                message: isRegistrationStep
                    ? EmailValidationResponseStatus.INVALID
                    : t('navigation:flyout.account.error.email'),
            });
        }
        else if (step === AuthFormStep.PASSWORD_FORGOTTEN) {
            return true;
        }
        else {
            const isCustomerEmailExist = yield emailExists(email);
            if (isCustomerEmailExist) {
                setFormStep(AuthFormStep.LOGIN);
                return true;
            }
            else {
                setFormStep(AuthFormStep.REGISTRATION);
                return yield validateEmailWithBackend(email, context, apiPrefix, emailStatus, setSuggestedEmail, setEmailStatus);
            }
        }
    }), EMAIL_VALIDATION_DEBOUNCE), [skipEmailValidation, emailStatus, step]);
    const onEmailChange = (email) => {
        setEmailFieldValue(email);
        skipEmailValidation && setSkipEmailValidation(false);
    };
    const onIgnoreSuggestedEmailClick = () => __awaiter(void 0, void 0, void 0, function* () {
        setSkipEmailValidation(true);
        const isCustomerEmailExist = emailFieldValue ? yield emailExists(emailFieldValue) : false;
        setFormStep(isCustomerEmailExist ? AuthFormStep.LOGIN : AuthFormStep.REGISTRATION);
    });
    const emailValidationSchema = Yup.object().shape({
        email: Yup.string().test(validateEmailField),
    });
    const loginValidationSchema = Yup.object().shape({
        password: Yup.string().required(t('account:delivery.required')),
    });
    const registrationValidationSchema = Yup.object().shape({
        title: Yup.string().required(t('account:delivery.required')),
        firstName: Yup.string().required(t('account:delivery.required')),
        lastName: Yup.string().required(t('account:delivery.required')),
        gender: Yup.string().required(t('account:delivery.required')),
    });
    const validationSchemas = {
        [AuthFormStep.EMAIL]: emailValidationSchema,
        [AuthFormStep.PASSWORD_FORGOTTEN]: emailValidationSchema,
        [AuthFormStep.LOGIN]: emailValidationSchema.concat(loginValidationSchema),
        [AuthFormStep.REGISTRATION]: emailValidationSchema
            .concat(loginValidationSchema)
            .concat(registrationValidationSchema),
    };
    const handleSubmit = (values) => {
        switch (step) {
            case AuthFormStep.LOGIN:
                return handleLoginSubmit(values);
            case AuthFormStep.REGISTRATION:
                return handleRegistrationSubmit(values);
            case AuthFormStep.PASSWORD_FORGOTTEN:
                return handlePasswordForgottenSubmit(values);
            default:
                break;
        }
    };
    const dpeAuthMessages = () => {
        switch (step) {
            case AuthFormStep.LOGIN:
                return data.loginMessage;
            case AuthFormStep.REGISTRATION:
                return data.registrationMessage;
            case AuthFormStep.PASSWORD_FORGOTTEN:
                return data.passwordForgottenMessage;
            case AuthFormStep.EMAIL:
            default:
                return data.introductionMessage;
        }
    };
    const handleLinkClick = () => {
        switch (step) {
            case AuthFormStep.LOGIN:
                return setFormStep(AuthFormStep.PASSWORD_FORGOTTEN);
            case AuthFormStep.PASSWORD_FORGOTTEN:
            default:
                return setFormStep(AuthFormStep.LOGIN);
        }
    };
    const handleAcceptClick = () => {
        setEmailStatus(`${EmailValidationResponseStatus.VALID}_${suggestedEmail}`);
        setEmailFieldValue(suggestedEmail);
    };
    useEffect(() => {
        if (step === AuthFormStep.LOGIN) {
            if (loginState && loginState.error !== responseErrorMessage) {
                setResponseErrorMessage(t(loginState.error));
            }
            if (loginState && loginState.loading !== loading) {
                setLoading(loginState.loading);
            }
        }
    }, [step, loginState]);
    useEffect(() => {
        getTitlesFromApi();
        getGendersFromApi();
    }, []);
    return (_jsxs(_Fragment, { children: [!responseSuccessMessage && (_jsx(Box, { paddingBlockEnd: {
                    base: step === AuthFormStep.PASSWORD_FORGOTTEN ? '8' : '6',
                    md: step === AuthFormStep.REGISTRATION ? '8' : '4',
                }, children: _jsx(TextAlpha, { variant: "bodyS", children: _jsx(HtmlInclude, { htmlFragment: dpeAuthMessages() }) }) })), responseErrorMessage && (_jsx(Box, { paddingBlock: "4", children: _jsx(Banner, { status: "error", text: responseErrorMessage }) })), responseSuccessMessage && (_jsx(Box, { paddingBlock: "4", children: _jsx(Banner, { status: "success", text: responseSuccessMessage }) })), _jsx(DpeAuthForm, { onSubmit: handleSubmit, validationSchema: validationSchemas[step], step: step, onLinkClick: handleLinkClick, customerGenders: customerGenders, customerTitles: customerTitles, loading: loading, onValidationError: onValidationError, emailErrorRender: msg => (_jsx(EmailError, { emailStatus: msg, suggestedEmail: suggestedEmail, requiredErrorLabel: t('account:delivery.required'), onAcceptClick: handleAcceptClick, onIgnoreClick: onIgnoreSuggestedEmailClick })), children: _jsx(EmailChangeHandler, { emailFieldKey: "email", emailValue: emailFieldValue, skipEmailValidation: skipEmailValidation, onEmailChange: onEmailChange, setEmailStatus: setEmailStatus }) })] }));
};
export default DpeAuth;
